import { isValidElement, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { docImage, slugify } from "../docs";

/**
 * Customer documentation, rendered from the repo's markdown at build time.
 * GitHub-flavored (tables, task lists); raw HTML is not rendered, so a doc
 * cannot carry a script. Headings get GitHub-style ids so a link written
 * by hand in one file reaches a heading in another, and "on this page" can
 * point at them. Classes are the portal's, not a typography plugin's, so
 * the docs read like the rest of the product.
 */
const textOf = (node: ReactNode): string =>
  Array.isArray(node) ? node.map(textOf).join("") : typeof node === "string" || typeof node === "number" ? String(node) : node && typeof node === "object" && "props" in node ? textOf((node as { props: { children?: ReactNode } }).props.children) : "";

/**
 * The reading measure (#534): prose - paragraphs, lists, quotes, headings -
 * stops at about 85 characters, since longer lines are hard to follow; a
 * table, a code block or a figure takes the article's whole width.
 */
const MEASURE = "max-w-[85ch]";

const heading = (level: 2 | 3 | 4) =>
  function Heading({ children }: { children?: ReactNode }) {
    const id = slugify(textOf(children));
    const cls = level === 2 ? "mt-8 scroll-mt-[calc(var(--page-bar)+1rem)] text-xl font-semibold" : level === 3 ? "mt-6 scroll-mt-[calc(var(--page-bar)+1rem)] text-base font-semibold" : "mt-4 scroll-mt-[calc(var(--page-bar)+1rem)] text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400";
    const Tag = `h${level}` as const;
    return (
      <Tag id={id} tabIndex={-1} className={`${MEASURE} ${cls}`}>
        {children}
      </Tag>
    );
  };

// Made once, not per render: a new component type on every render would remount each heading and drop the focus a jump put on it (#417).
const H2 = heading(2);
const H3 = heading(3);
const H4 = heading(4);

/**
 * A fenced block (#421): its lines as written - a diagram is drawn in
 * them, so they never wrap - scrolling sideways inside its own box when
 * wider than the column. A block that scrolls takes keyboard focus and is
 * named, so a keyboard can scroll it too (axe scrollable-region-focusable);
 * one that fits is not an extra tab stop. The fence's <code> is read from
 * its props: without a language react-markdown gives it no class, which
 * once made a diagram render as inline code and spill over the rail.
 * A copy button sits on its top right (the owner, 1 Oct): what it copies is
 * the block's text exactly as shown.
 */
function CodeBlock({ children }: { children?: ReactNode }) {
  const ref = useRef<HTMLPreElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setScrolls(el.scrollWidth > el.clientWidth);
    measure();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [copied, setCopied] = useState<"yes" | "no">();
  const code = isValidElement<{ className?: string; children?: ReactNode }>(children) ? children.props : { children };
  const language = /language-(\S+)/.exec(code.className ?? "")?.[1];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ref.current?.textContent ?? "");
      setCopied("yes");
    } catch {
      setCopied("no");
    }
    setTimeout(() => setCopied(undefined), 2000);
  };
  return (
    <div className="relative mt-3">
      <pre
        ref={ref}
        tabIndex={scrolls ? 0 : undefined}
        role={scrolls ? "region" : undefined}
        aria-label={scrolls ? `${language ? `Code (${language})` : "Code"}, scrolls sideways` : undefined}
        className="max-w-full overflow-x-auto whitespace-pre rounded-lg bg-neutral-100 p-3 pr-12 font-mono text-xs leading-5 dark:bg-neutral-800"
      >
        <code>{code.children}</code>
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={language ? `Copy code (${language})` : "Copy code"}
        className="absolute right-2 top-2 rounded-md border border-neutral-300 bg-white/90 p-1.5 text-neutral-600 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent dark:border-neutral-600 dark:bg-neutral-900/90 dark:text-neutral-300 dark:hover:text-white"
      >
        {copied === "yes" ? (
          <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 fill-current"><path d="M7.7 13.3 4.4 10l-1.1 1.1 4.4 4.4 9-9-1.1-1.1z" /></svg>
        ) : (
          <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.6"><rect x="7" y="7" width="10" height="10" rx="1.5" /><path d="M13 7V4.5A1.5 1.5 0 0 0 11.5 3h-7A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13H7" /></svg>
        )}
      </button>
      <span role="status" className="sr-only">{copied === "yes" ? "Copied" : copied === "no" ? "Couldn’t copy; select the text instead" : ""}</span>
    </div>
  );
}

/**
 * An image in a document (#534): written in the markdown as a path beside the
 * file (`images/x.png`), so it also shows on GitHub, and resolved here to the
 * bundled asset in DOC_IMAGES. Drawn at its own size or the column's,
 * whichever is smaller, with its dimensions reserved so the page does not
 * move under a jump from "On this page" while it loads; it links to itself
 * for a reader whose column shrinks it. An image the registry does not hold
 * is not rendered: a doc cannot point the portal at another host.
 */
function DocImage({ src, alt }: { src: string; alt: string }) {
  const image = docImage(src);
  if (!image) return null;
  return (
    <a href={image.url} target="_blank" rel="noopener noreferrer" className="block w-fit max-w-full" title="Open full size">
      <img src={image.url} alt={alt} width={image.width} height={image.height} className="block h-auto max-w-full" />
    </a>
  );
}

export function Markdown({ source }: { source: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // The page title is the document's `#`; a `#` inside the body is demoted so the page keeps one h1.
        h1: H2,
        h2: H2,
        h3: H3,
        h4: H4,
        // A paragraph that is only an image is a figure: it takes the article's width, not the reading measure.
        p: ({ node, children }) =>
          node?.children.length === 1 && node.children[0]?.type === "element" && node.children[0].tagName === "img" ? (
            <div className="mt-4">{children}</div>
          ) : (
            <p className={`${MEASURE} mt-3 text-sm leading-6`}>{children}</p>
          ),
        img: ({ src, alt }) => <DocImage src={typeof src === "string" ? src : ""} alt={alt ?? ""} />,
        ul: ({ children }) => <ul className={`${MEASURE} mt-3 list-disc space-y-1 pl-5 text-sm leading-6`}>{children}</ul>,
        ol: ({ children }) => <ol className={`${MEASURE} mt-3 list-decimal space-y-1 pl-5 text-sm leading-6`}>{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        a: ({ href, children }) => {
          const external = Boolean(href && /^https?:/.test(href));
          return (
            <a href={href} className="text-accent-strong underline hover:text-accent dark:text-accent" {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
              {children}
            </a>
          );
        },
        // Inline code only: a fenced block's <code> is rendered by CodeBlock, which reads it from its <pre>.
        code: ({ children }) => <code className="break-words rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.85em] dark:bg-neutral-800">{children}</code>,
        pre: CodeBlock,
        blockquote: ({ children }) => <blockquote className="mt-3 max-w-[85ch] border-l-2 border-neutral-300 pl-3 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-400">{children}</blockquote>,
        table: ({ children }) => (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-600 dark:border-neutral-800 dark:text-neutral-400">{children}</thead>,
        th: ({ children }) => <th className="py-2 pr-4 font-medium">{children}</th>,
        td: ({ children }) => <td className="border-b border-neutral-100 py-2 pr-4 align-top leading-6 dark:border-neutral-800/60">{children}</td>,
        hr: () => <hr className="my-6 border-neutral-200 dark:border-neutral-800" />,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      }}
    >
      {source}
    </ReactMarkdown>
  );
}
